import { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import {
  getLoadByStatus,
  getLoadByTopBaseStatus,
  getLoadHistory,
  getLoadHistoryByTopBase,
  getLoadStatistics,
  getLoadStatisticsByTopBase,
} from 'service/LoadService';
import { getBlockedDrivers } from 'service/TabelService';
import { getRecentLoads, getLoadsCompleted, getLoadsInLoading } from 'service/ReportsService';
import { getTabCounts } from 'service/DriverRegistrationService';

/**
 * Хук для загрузки перевозок (in_route), истории, статистики и заблокированных водителей.
 *
 * @param {Object} opts
 * @param {boolean}  opts.isGuard
 * @param {Object}   opts.isTopBaseOperator
 * @param {Object}   opts.isChppOperator
 * @param {Object}   opts.isPrivatePointOperator
 * @param {Object}   opts.user
 * @param {boolean}  opts.refresh    — триггер обновления (toggle)
 * @param {Function} opts.onNotify
 */
const useLoads = ({
  isGuard,
  isTopBaseOperator,
  isChppOperator,
  isPrivatePointOperator,
  user,
  refresh,
  onNotify,
  showInLoading,
  showInRoute,
  showHistory,
  showAnalytics,
}) => {
  // Always-on tab badge counts (polled every 30 s)
  const [tabCounts, setTabCounts] = useState({ in_loading: 0, in_route: 0, completed: 0, drivers: 0 });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await getTabCounts();
        if (res?.data) setTabCounts(res.data);
      } catch (_) {/* silent */}
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  // Last 3 marks
  const [last3Marks, setLast3Marks] = useState([]);
  const [last3Loading, setLast3Loading] = useState(false);

  // Определяем ID базы оператора
  const getOperatorBaseId = () => {
    if (isTopBaseOperator && isTopBaseOperator.top_base) return isTopBaseOperator.top_base;
    if (isChppOperator && isChppOperator.chpp) return isChppOperator.chpp;
    if (isPrivatePointOperator && isPrivatePointOperator.private_point) return isPrivatePointOperator.private_point;
    return null;
  };

  const operatorBaseId = getOperatorBaseId();
  const isAnyOperator = !!(isTopBaseOperator || isChppOperator || isPrivatePointOperator);

  // In-loading loads
  const [inLoadingLoads, setInLoadingLoads] = useState([]);
  const [inLoadingCount, setInLoadingCount] = useState(0);
  const [inLoadingNext, setInLoadingNext] = useState(null);
  const [inLoadingPrev, setInLoadingPrev] = useState(null);
  const [inLoadingPage, setInLoadingPage] = useState(1);
  const [inLoadingSearch, setInLoadingSearch] = useState('');

  // In-route loads
  const [inRouteLoads, setInRouteLoads] = useState([]);
  const [inRouteCount, setInRouteCount] = useState(0);
  const [inRouteNext, setInRouteNext] = useState(null);
  const [inRoutePrev, setInRoutePrev] = useState(null);
  const [inRoutePage, setInRoutePage] = useState(1);
  const [inRouteSearch, setInRouteSearch] = useState('');

  // Completed loads
  const [completedLoads, setCompletedLoads] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [completedNext, setCompletedNext] = useState(null);
  const [completedPrev, setCompletedPrev] = useState(null);
  const [completedPage, setCompletedPage] = useState(1);
  const [completedSearch, setCompletedSearch] = useState('');

  // History
  const [arrivedBLoads, setArrivedBLoads] = useState([]);
  const [arrivedBCount, setArrivedBCount] = useState(0);
  const [arrivedBNext, setArrivedBNext] = useState(null);
  const [arrivedBPrev, setArrivedBPrev] = useState(null);
  const [arrivedBPage, setArrivedBPage] = useState(1);
  const [arrivedBSearch, setArrivedBSearch] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTableLoading, setHistoryTableLoading] = useState(false);

  // Statistics
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);

  // Blocked drivers
  const [blockedDrivers, setBlockedDrivers] = useState([]);
  const [blockedDriversLoading, setBlockedDriversLoading] = useState(false);
  const [blockedDriversError, setBlockedDriversError] = useState(null);
  const [blockedDriversPage, setBlockedDriversPage] = useState(1);
  const [blockedDriversCount, setBlockedDriversCount] = useState(0);
  const BLOCKED_PAGE_SIZE = 10;

  // Time
  const [nowTime, setNowTime] = useState(moment());

  // Сброс страницы при изменении поиска
  useEffect(() => { setInLoadingPage(1); }, [inLoadingSearch]);
  useEffect(() => { setInRoutePage(1); }, [inRouteSearch]);
  useEffect(() => { setCompletedPage(1); }, [completedSearch]);
  useEffect(() => { setArrivedBPage(1); }, [arrivedBSearch]);

  // Текущее время (обновляется каждую секунду)
  useEffect(() => {
    const interval = setInterval(() => setNowTime(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Загрузка последних 3 отметок (В погрузке)
  useEffect(() => {
    const fetchLast3 = async () => {
      setLast3Loading(true);
      try {
        const res = await getLoadsInLoading({ page: 1, page_size: 3 });
        const data = res?.data?.results || [];
        setLast3Marks(data);
      } catch (e) {
        console.error('Ошибка загрузки последних отметок:', e);
      } finally {
        setLast3Loading(false);
      }
    };
    if (user?.is_admin_of || user?.is_report_of || isGuard) {
      fetchLast3();
    }
  }, [user?.is_admin_of, user?.is_report_of, isGuard, refresh]);

  // Загрузка загрузок "В погрузке"
  useEffect(() => {
    if (!showInLoading) return;

    const fetchInLoading = async () => {
      try {
        const params = { page: inLoadingPage, page_size: 10 };
        if (inLoadingSearch) params.search = inLoadingSearch;
        
        let data = [];
        let count = 0;
        let next = null;
        let prev = null;

        if (user?.is_admin_of || user?.is_report_of || isGuard) {
          const res = await getLoadsInLoading(params);
          data = res.data.results || [];
          count = res.data.count || 0;
          next = res.data.next;
          prev = res.data.previous;
        } else if (isAnyOperator && operatorBaseId) {
          // Используем отдельный endpoint для базы, передавая в params статус "В погрузке"
          // Если api getLoadByTopBaseStatus поддерживает status, то нужно передать
          // Здесь assumed что getLoadByTopBaseStatus вызывает /tabel/loads-by-topbase/{id}/
          // и мы добавим status в params
          params.status = 'В погрузке';
          const res = await getLoadByTopBaseStatus(operatorBaseId, params);
          data = Array.isArray(res.data.results?.in_loading) ? res.data.results.in_loading : [];
          count = res.data.results?.in_loading_count || res.data.count || 0;
          next = (inLoadingPage * 10 < count) ? 'next' : null;
          prev = (inLoadingPage > 1) ? 'prev' : null;
        }

        setInLoadingLoads(data);
        setInLoadingCount(count);
        setInLoadingNext(next);
        setInLoadingPrev(prev);

      } catch (e) {
        console.error('Ошибка загрузки погрузок "В погрузке":', e);
      }
    };
    
    if (user?.is_admin_of || user?.is_report_of || isGuard || (isAnyOperator && operatorBaseId)) {
      fetchInLoading();
    }
  }, [user?.is_admin_of, user?.is_report_of, isGuard, isAnyOperator, operatorBaseId, inLoadingPage, inLoadingSearch, refresh, showInLoading]);

  // Загрузка завершенных загрузок
  useEffect(() => {
    if (!showHistory) return;

    const fetchCompleted = async () => {
      try {
        const params = { page: completedPage, page_size: 10 };
        if (completedSearch) params.search = completedSearch;

        let data = [];
        let count = 0;
        let next = null;
        let prev = null;

        if (user?.is_admin_of || user?.is_report_of || isGuard) {
          const res = await getLoadsCompleted(params);
          data = res.data.results || [];
          count = res.data.count || 0;
          next = res.data.next;
          prev = res.data.previous;
        } else if (isAnyOperator && operatorBaseId) {
          const res = await getLoadHistoryByTopBase(operatorBaseId, params);
          // Структура ответа для истории по базе может отличаться (arrived_b)
          data = Array.isArray(res.data.results?.arrived_b) ? res.data.results.arrived_b : (res.data.results || []);
          count = res.data.results?.arrived_b_count || res.data.count || 0;
          next = (completedPage * 10 < count) ? 'next' : null; // Или из res.data.next если есть
          prev = (completedPage > 1) ? 'prev' : null;
        }

        setCompletedLoads(data);
        setCompletedCount(count);
        setCompletedNext(next);
        setCompletedPrev(prev);

      } catch (e) {
        console.error('Ошибка загрузки завершенных загрузок:', e);
        // onNotify({ isOpen: true, message: 'Ошибка загрузки истории', type: 'error' });
      }
    };

    if (user?.is_admin_of || user?.is_report_of || isGuard || (isAnyOperator && operatorBaseId)) {
        fetchCompleted();
    }
  }, [user?.is_admin_of, user?.is_report_of, isGuard, isAnyOperator, operatorBaseId, completedPage, completedSearch, refresh, showHistory]);

  // Загрузка заблокированных водителей
  const fetchBlockedDrivers = useCallback(async (page = 1) => {
    if (!user?.is_admin_of && !user?.is_report_of) {
      setBlockedDrivers([]);
      setBlockedDriversCount(0);
      return;
    }
    setBlockedDriversLoading(true);
    setBlockedDriversError(null);
    try {
      const response = await getBlockedDrivers({ page, page_size: BLOCKED_PAGE_SIZE });
      setBlockedDrivers(response.data.results || []);
      setBlockedDriversCount(response.data.count || 0);
    } catch (error) {
      setBlockedDriversError('Ошибка загрузки заблокированных водителей');
    } finally {
      setBlockedDriversLoading(false);
    }
  }, [user?.is_admin_of, user?.is_report_of]);


  // Загрузка in-route перевозок
  useEffect(() => {
    if (!showInRoute) return;

    const fetchLoads = async () => {
      try {
        if (isGuard || user?.is_admin_of || user?.is_report_of) {
          const params = { page: inRoutePage, page_size: 10 };
          if (inRouteSearch) params.search = inRouteSearch;
          const res = await getLoadByStatus(params);
          const inRouteData = Array.isArray(res.data.results?.active) ? res.data.results.active : [];
          const totalInRoute = res.data.results?.active_count || 0;
          setInRouteLoads(inRouteData);
          setInRouteCount(totalInRoute);
          setInRouteNext(inRoutePage * 10 < totalInRoute ? 'next' : null);
          setInRoutePrev(inRoutePage > 1 ? 'prev' : null);
        } else if (isAnyOperator && operatorBaseId) {
          const params = { page: inRoutePage, page_size: 10 };
          if (inRouteSearch) params.search = inRouteSearch;
          const res = await getLoadByTopBaseStatus(operatorBaseId, params);
          const inRouteData = Array.isArray(res.data.results?.in_route) ? res.data.results.in_route : [];
          const totalInRoute = res.data.results?.in_route_count || 0;
          setInRouteLoads(inRouteData);
          setInRouteCount(totalInRoute);
          setInRouteNext(inRoutePage * 10 < totalInRoute ? 'next' : null);
          setInRoutePrev(inRoutePage > 1 ? 'prev' : null);
        }
      } catch (e) {
        onNotify({ isOpen: true, message: 'Ошибка загрузки перевозок', type: 'error' });
      }
    };
    
    fetchLoads();

    // Автообновление каждые 30 сек для админов, если открыто
    let autoRefreshInterval;
    if (user?.is_admin_of || user?.is_report_of) {
      autoRefreshInterval = setInterval(() => {
        fetchLoads();
      }, 30000);
    }
    return () => { if (autoRefreshInterval) clearInterval(autoRefreshInterval); };
  }, [isAnyOperator, operatorBaseId, isGuard, user?.is_admin_of, user?.is_report_of, refresh, inRoutePage, inRouteSearch, showInRoute]);

  // Загрузка заблокированных водителей (отдельно для аналитики)
  useEffect(() => {
    if (!showAnalytics) return;
    fetchBlockedDrivers(blockedDriversPage);
  }, [showAnalytics, fetchBlockedDrivers, refresh, blockedDriversPage]);


  // Загрузка истории по клику
  const loadHistory = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setHistoryLoading(true);
      setShowHistoryModal(true);
    } else {
      setHistoryTableLoading(true);
    }
    try {
      if (isGuard || user?.is_admin_of || user?.is_report_of) {
        const params = { page: arrivedBPage, page_size: 10 };
        if (arrivedBSearch) params.search = arrivedBSearch;
        const res = await getLoadHistory(params);
        const arrivedData = Array.isArray(res.data.results?.arrived_b) ? res.data.results.arrived_b : [];
        const totalArrived = res.data.results?.arrived_b_count || 0;
        setArrivedBLoads(arrivedData);
        setArrivedBCount(totalArrived);
        setArrivedBNext(arrivedBPage * 10 < totalArrived ? 'next' : null);
        setArrivedBPrev(arrivedBPage > 1 ? 'prev' : null);
      } else if (isAnyOperator && operatorBaseId) {
        const params = { page: arrivedBPage, page_size: 10 };
        if (arrivedBSearch) params.search = arrivedBSearch;
        const res = await getLoadHistoryByTopBase(operatorBaseId, params);
        const arrivedData = Array.isArray(res.data.results?.arrived_b) ? res.data.results.arrived_b : [];
        const totalArrived = res.data.results?.arrived_b_count || 0;
        setArrivedBLoads(arrivedData);
        setArrivedBCount(totalArrived);
        setArrivedBNext(arrivedBPage * 10 < totalArrived ? 'next' : null);
        setArrivedBPrev(arrivedBPage > 1 ? 'prev' : null);
      }
    } catch (e) {
      onNotify({ isOpen: true, message: 'Ошибка загрузки истории', type: 'error' });
    } finally {
      if (isInitialLoad) setHistoryLoading(false);
      else setHistoryTableLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuard, isAnyOperator, operatorBaseId, user?.is_admin_of, user?.is_report_of, arrivedBPage, arrivedBSearch]);

  // Перезагрузка при изменении страницы истории
  useEffect(() => {
    if (showHistoryModal) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedBPage]);

  // Debounce для поиска по истории
  useEffect(() => {
    if (!showHistoryModal) return;
    const timer = setTimeout(() => {
      if (arrivedBPage === 1) loadHistory();
      else setArrivedBPage(1);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedBSearch]);

  // Загрузка статистики
  useEffect(() => {
    if (!showAnalytics) return;

    const fetchStatistics = async () => {
      setStatsLoading(true);
      try {
        let res;
        if (isGuard || user?.is_admin_of || user?.is_report_of) {
          res = await getLoadStatistics();
        } else if (isTopBaseOperator && isTopBaseOperator.top_base) {
          res = await getLoadStatisticsByTopBase(isTopBaseOperator.top_base);
        }
        if (res?.data) setStatsData(res.data);
      } catch (e) {
        console.error('Ошибка загрузки статистики:', e);
      } finally {
        setStatsLoading(false);
      }
    };
    if (isGuard || isTopBaseOperator || user?.is_admin_of || user?.is_report_of) {
      fetchStatistics();
    }
  }, [isTopBaseOperator, isGuard, user?.is_admin_of, user?.is_report_of, refresh, showAnalytics]);

  return {
    // Last 3 marks
    last3Marks, last3Loading,
    // In-loading
    inLoadingLoads, inLoadingCount, inLoadingNext, inLoadingPrev,
    inLoadingPage, setInLoadingPage,
    inLoadingSearch, setInLoadingSearch,
    // In-route
    inRouteLoads, inRouteCount, inRouteNext, inRoutePrev,
    inRoutePage, setInRoutePage,
    inRouteSearch, setInRouteSearch,
    // Completed
    completedLoads, completedCount, completedNext, completedPrev,
    completedPage, setCompletedPage,
    completedSearch, setCompletedSearch,
    // History
    arrivedBLoads, arrivedBCount, arrivedBNext, arrivedBPrev,
    arrivedBPage, setArrivedBPage,
    arrivedBSearch, setArrivedBSearch,
    showHistoryModal, setShowHistoryModal,
    historyLoading, historyTableLoading,
    loadHistory,
    // Stats
    statsLoading, statsData,
    // Blocked
    blockedDrivers, blockedDriversLoading, blockedDriversError,
    blockedDriversPage, setBlockedDriversPage, blockedDriversCount, BLOCKED_PAGE_SIZE,
    // Time
    nowTime,
    // Tab counts (polled independently)
    tabCounts,
  };
};

export default useLoads;
